/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  Box,
  Stack,
  Typography,
  Alert,
  AlertTitle,
  Button,
  DialogContent,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { Message, MessageType } from '@marageti/z4-sdk/lib/travel';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { LoadingButton } from '@mui/lab';
import { getSubmitButtonProps, RichTextEditor, useApiClient } from '@marageti/z4-lib';

const ACTIONS = [{
  title: 'Phone Call: Traveler Answered',
  value: 'call-answered',
  messageType: MessageType.PhoneCall,
  snackbarMessage: 'Phone call log created.',
}, {
  title: 'Phone Call: No Answer',
  value: 'call-no-answer',
  messageType: MessageType.PhoneCallUnanswered,
  snackbarMessage: 'Phone call log created.',
}, {
  title: 'Text Message Sent',
  value: 'text',
  messageType: MessageType.SMS,
  snackbarMessage: 'Text message log created.',
}];

const schema = z.object({
  action: z.enum(['call-answered', 'call-no-answer', 'text']),
  message: z.any(),
});

type LogCallFormData = z.infer<typeof schema>;

const LogCallModal = ({
  open,
  onClose,
  conversationId,
  onAfterMessageSent,
}: {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  /** When set (e.g. conversation screen), refresh the thread after post — log types do not trigger Pusher TEXT. */
  onAfterMessageSent?: () => void | Promise<void>;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState<Error | null>(null);
  const apiClient = useApiClient();

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    control,
    watch,
    reset: resetForm,
  } = useForm<LogCallFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      action: 'call-answered',
      message: '',
    },
  });

  const reset = () => {
    setError(null);
    resetForm();
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const onSubmit: SubmitHandler<LogCallFormData> = async (data) => {
    const { message, action } = data;

    const actionObject = ACTIONS.find((act) => act.value === action);

    if (!actionObject) {
      throw new Error('Action not found');
    }

    const messageToSend: Partial<Message> = {
      conversationId,
      text: message,
      title: actionObject.title,
      messageType: actionObject.messageType,
    };

    try {
      await apiClient.conversationsClient.postMessage(conversationId, messageToSend);
      if (onAfterMessageSent) {
        await onAfterMessageSent();
      }
      enqueueSnackbar(actionObject?.snackbarMessage, {
        variant: 'success',
      });
      handleClose();
    } catch (e) {
      setError(e as Error);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ px: 3 }}>Log Call / Text Message</DialogTitle>
      <DialogContent sx={{
        p: 0,
        m: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      >
        {error && (
        <Alert severity="error">
          <AlertTitle>There was an error submitting log</AlertTitle>
          {error?.message}
        </Alert>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack
            spacing={2}
            sx={{
              px: 3,
              pt: 1,
              pb: 2.5,
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <Controller
              name="action"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field}>
                  {ACTIONS.map((act) => (
                    <FormControlLabel
                      key={act.value}
                      value={act.value}
                      control={<Radio />}
                      label={act.title}
                    />
                  ))}
                </RadioGroup>
              )}
            />
            <Box>
              <Controller
                control={control}
                name="message"
                render={({ field }) => (
                  <RichTextEditor
                    editorName="Log-Editor"
                    error={!!errors.message}
                    helperText={errors.message?.message as string}
                    label={watch('action') === 'text' ? 'Message Notes (Optional)' : 'Call Notes (Optional)'}
                    onChange={field.onChange}
                    useLinks
                    useLists
                    sx={{
                      maxHeight: '250px',
                      minHeight: '150px',
                      flex: 1,
                    }}
                  />
                )}
              />
              <Typography variant="caption" color="text.secondary">
                Call and Text Logs are internal only, not visible to the other party.
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={(theme) => ({
              borderTop: `1px solid ${theme.palette.divider}`,
              py: 2.5,
              px: 3,
              flexShrink: 0,
            })}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{
                width: { desktop: 'fit-content' },
              }}
              {...getSubmitButtonProps(isSubmitting || !open, isValid)}
            >
              Submit
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                width: { desktop: 'fit-content' },
              }}
            >
              Cancel
            </Button>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LogCallModal;
